/**
 * A phone-style preview of a WhatsApp template message: the chat wallpaper, one business message bubble (header, body,
 * footer, time) and its buttons underneath, as WhatsApp shows them. Light and dark follow the console theme.
 */

import { IconExternalLink, IconPhone, IconArrowBackUp, IconBrandWhatsapp } from '@tabler/icons-react';
import { useComputedColorScheme } from '@mantine/core';
import { Box, Group, Stack, Text } from '@/components/ui';
import type { PreviewParts } from './whatsappTemplateRules';

/** WhatsApp formatting: *bold*, _italic_, ~strike~ (enough for a preview). */
function Formatted({ text }: { text: string }) {
  const parts = text.split(/(\*[^*\n]+\*|_[^_\n]+_|~[^~\n]+~)/g);
  return (
    <>
      {parts.map((p, i) => {
        if (/^\*[^*]+\*$/.test(p)) return <strong key={i}>{p.slice(1, -1)}</strong>;
        if (/^_[^_]+_$/.test(p)) return <em key={i}>{p.slice(1, -1)}</em>;
        if (/^~[^~]+~$/.test(p)) return <s key={i}>{p.slice(1, -1)}</s>;
        return <span key={i}>{p}</span>;
      })}
    </>
  );
}

export function WhatsAppPhonePreview({ parts, title = 'Acts Christian Church', compact }: { parts: PreviewParts; title?: string; compact?: boolean }) {
  const dark = useComputedColorScheme('light') === 'dark';
  const c = dark
    ? { frame: '#0b0d0e', bar: '#1f2c34', wall: '#0b141a', bubble: '#1f2c34', text: '#e9edef', muted: '#8696a0', link: '#53bdeb', border: '#2a3942' }
    : { frame: '#1c1c1e', bar: '#008069', wall: '#efeae2', bubble: '#ffffff', text: '#111b21', muted: '#667781', link: '#027eb5', border: '#e9edef' };
  const width = compact ? 260 : 300;

  return (
    <Box
      style={{
        width,
        maxWidth: '100%',
        marginInline: 'auto',
        borderRadius: 34,
        padding: 9,
        background: c.frame,
        border: dark ? '1px solid #3b4a54' : undefined,
        boxShadow: '0 10px 30px rgba(0,0,0,0.25)',
      }}
      aria-label="Phone preview of the WhatsApp message"
    >
      <Box style={{ borderRadius: 26, overflow: 'hidden', background: c.wall }}>
        <Group gap={8} px="sm" py={10} style={{ background: c.bar }} wrap="nowrap">
          <Box style={{ width: 28, height: 28, borderRadius: 14, background: '#25d366', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <IconBrandWhatsapp size={18} color="#fff" />
          </Box>
          <Stack gap={0} style={{ minWidth: 0 }}>
            <Text size="sm" fw={600} c="#fff" truncate>
              {title}
            </Text>
            <Text size="10px" c="rgba(255,255,255,0.75)">
              Business account
            </Text>
          </Stack>
        </Group>

        <Box p="sm" style={{ minHeight: compact ? 220 : 300 }}>
          <Box style={{ maxWidth: '88%', borderRadius: 8, borderTopLeftRadius: 2, background: c.bubble, boxShadow: '0 1px 0.5px rgba(11,20,26,0.13)', overflow: 'hidden' }}>
            <Stack gap={4} px={9} pt={7} pb={4}>
              {parts.headerText && (
                <Text size="sm" fw={700} c={c.text}>
                  {parts.headerText}
                </Text>
              )}
              <Text size="sm" c={c.text} style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', lineHeight: 1.4 }}>
                {parts.bodyText ? <Formatted text={parts.bodyText} /> : <span style={{ color: c.muted }}>Your message appears here.</span>}
              </Text>
              {parts.footerText && (
                <Text size="xs" c={c.muted}>
                  {parts.footerText}
                </Text>
              )}
              <Text size="10px" c={c.muted} ta="right">
                12:00
              </Text>
            </Stack>
            {parts.buttons.map((b, i) => (
              <Group key={i} justify="center" gap={6} py={8} style={{ borderTop: `1px solid ${c.border}` }} wrap="nowrap">
                {b.type === 'URL' ? <IconExternalLink size={15} color={c.link} /> : b.type === 'PHONE_NUMBER' ? <IconPhone size={15} color={c.link} /> : <IconArrowBackUp size={15} color={c.link} />}
                <Text size="sm" fw={500} c={c.link} truncate>
                  {b.text || 'Button'}
                </Text>
              </Group>
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
